import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Network } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import epcProjectService, {
  type EpcProject,
  type EpcProjectHealth,
} from '../../../services/epcProjectService';
import { useEpcProjectDetail } from './EpcProjectDetailContext';
import { deriveProjectProgress } from './card/epcProjectProgress';
import { OverviewHeader, type HealthTone } from './overview/OverviewHeader';
import { OverviewSummaryStrip } from './overview/OverviewSummaryStrip';
import { RevisionTimelineCard } from './overview/RevisionTimelineCard';
import { RevisionLogCard } from './overview/RevisionLogCard';
import { ProjectFactsCard, type SiteFact } from './overview/ProjectFactsCard';
import { BriefingNotesCard } from './overview/BriefingNotesCard';
import { deriveMilestones } from './overview/revisionLanes';
import { PmoCard } from './overview/ui/PmoCard';

const HEALTH_META: Record<EpcProjectHealth, { label: string; tone: HealthTone }> = {
  on_track: { label: 'On track', tone: 'ok' },
  at_risk: { label: 'At risk', tone: 'watch' },
  delayed: { label: 'Delayed', tone: 'bad' },
  completed: { label: 'Completed', tone: 'done' },
  upcoming: { label: 'Upcoming', tone: 'muted' },
  not_configured: { label: 'Planning', tone: 'muted' },
};

function refName(ref: unknown): string {
  if (ref && typeof ref === 'object' && 'name' in ref) {
    return String((ref as { name?: string }).name ?? '');
  }
  return '';
}

function locationsLine(project: EpcProject): string {
  const locs = Array.isArray(project.locationIds) ? project.locationIds : [];
  const names = locs.map((loc) => refName(loc)).filter(Boolean);
  if (names.length) return names.join(', ');
  return refName(project.locationId) || '—';
}

function toNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** Parse the project's populated location refs into map-ready site facts. */
function siteFacts(project: EpcProject): SiteFact[] {
  const raw = Array.isArray(project.locationIds) && project.locationIds.length
    ? project.locationIds
    : project.locationId
      ? [project.locationId]
      : [];
  const out: SiteFact[] = [];
  for (const loc of raw) {
    if (!loc || typeof loc !== 'object') continue;
    const rec = loc as { _id?: unknown; id?: unknown; name?: string; state?: string; latitude?: unknown; longitude?: unknown };
    const id = String(rec._id ?? rec.id ?? '');
    if (!id || !rec.name) continue;
    out.push({
      id,
      name: rec.name,
      ...(rec.state ? { state: rec.state } : {}),
      ...(toNum(rec.latitude) !== undefined ? { lat: toNum(rec.latitude) } : {}),
      ...(toNum(rec.longitude) !== undefined ? { lng: toNum(rec.longitude) } : {}),
    });
  }
  return out;
}

function ImportScheduleCallout({ canImport, onImport }: { canImport: boolean; onImport: () => void }) {
  return (
    <PmoCard className="mb-4">
      <div className="px-4 py-5">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-slate-600" />
          <h4 className="text-[13.5px] font-bold text-slate-900">Baseline schedule not linked</h4>
        </div>
        <p className="mt-1.5 text-[12px] text-slate-600">
          Import a <code className="rounded bg-[#f8fafc] px-1 py-0.5 font-mono ring-1 ring-[#e2e8f0]">.mpp</code> file
          from the Schedule workspace to load tasks and unlock the revision timeline.
        </p>
        {!canImport && <p className="mt-1.5 text-[11px] font-medium text-[#b45309]">Import permission required.</p>}
        <button
          type="button"
          onClick={onImport}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#006346] px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#076333]"
        >
          MPP import
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </PmoCard>
  );
}

export type EpcProjectOverviewProps = {
  projectId: string;
  project: EpcProject;
  hasSchedule: boolean;
  canUpdate: boolean;
};

export default function EpcProjectOverview({ projectId, project, hasSchedule }: EpcProjectOverviewProps) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { snapshot } = useEpcProjectDetail();
  const canImport = hasPermission('epcProject', 'import');

  const revisionsQ = useQuery({
    queryKey: ['epc-project', projectId, 'schedule-revisions'],
    queryFn: () => epcProjectService.getScheduleRevisions(projectId),
    staleTime: 60_000,
  });
  const orgUnitsQ = useQuery({
    queryKey: ['epc-project', projectId, 'org-units'],
    queryFn: () => epcProjectService.listOrgUnits(projectId),
    staleTime: 60_000,
  });
  const membershipsQ = useQuery({
    queryKey: ['epc-project', projectId, 'memberships'],
    queryFn: () => epcProjectService.listMemberships(projectId),
    staleTime: 60_000,
  });

  const revisions = revisionsQ.data ?? [];
  const revisionsUnavailable = revisionsQ.isError;
  const activeOrgUnits = (orgUnitsQ.data ?? []).filter((u) => !u.inactive);
  const activeMembers = (membershipsQ.data ?? []).filter((m) => !m.inactive);

  const derived = deriveProjectProgress({ project });
  const plannedPct = Math.round(derived.plannedPct);
  const actualPct = derived.actualPct == null ? null : Math.round(derived.actualPct);
  const progressPct = actualPct ?? plannedPct;
  const health = HEALTH_META[derived.health];

  const tasks = useMemo(
    () => (Array.isArray(snapshot?.tasks) ? (snapshot.tasks as Array<Record<string, unknown>>) : []),
    [snapshot],
  );
  const milestones = useMemo(() => deriveMilestones(tasks), [tasks]);

  const customer = refName(project.customerId);
  const subLine = [
    project.code,
    customer || null,
    locationsLine(project),
    project.projectManager ? `PM: ${project.projectManager}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const currentRevision = revisions.length > 0 ? revisions[revisions.length - 1].version : null;

  const timelineProject = useMemo(
    () => ({
      ...(project.startDate ? { startDate: project.startDate } : {}),
      ...(project.endDatePlanned ? { endDatePlanned: project.endDatePlanned } : {}),
      ...(project.commissioningDate ? { commissioningDate: project.commissioningDate } : {}),
    }),
    [project.startDate, project.endDatePlanned, project.commissioningDate],
  );

  return (
    <div className="w-full rounded-[14px] bg-[#FAFBFA] p-3 font-[family-name:var(--font-outfit)] sm:p-4">
      <OverviewHeader
        name={project.name}
        subLine={subLine}
        updatedAt={project.updatedAt}
        healthLabel={health.label}
        healthTone={health.tone}
        progressPct={progressPct}
      />
      <OverviewSummaryStrip
        progressPct={progressPct}
        departments={orgUnitsQ.isSuccess ? activeOrgUnits.length : null}
        scheduleLines={project.scheduleStats?.taskCount ?? null}
        wbsDepth={project.scheduleStats?.maxOutlineLevel ?? null}
        dcCapacityMw={project.dcCapacityMw ?? null}
        acCapacityMw={project.acCapacityMw ?? null}
        teamCount={membershipsQ.isSuccess ? activeMembers.length : null}
        currentRevision={revisionsUnavailable ? null : currentRevision}
      />
      {hasSchedule ? (
        <RevisionTimelineCard
          revisions={revisions}
          revisionsUnavailable={revisionsUnavailable}
          project={timelineProject}
          milestones={milestones}
          plannedTodayPct={plannedPct}
          actualPct={actualPct}
        />
      ) : (
        <ImportScheduleCallout canImport={canImport} onImport={() => navigate('../mpp', { relative: 'path' })} />
      )}
      <div className="grid gap-4 lg:grid-cols-[1.85fr_1fr]">
        <RevisionLogCard revisions={revisions} unavailable={revisionsUnavailable} />
        <div className="flex flex-col gap-4">
          <ProjectFactsCard
            code={project.code}
            name={project.name}
            customer={customer || '—'}
            projectHead={activeMembers.find((m) => m.role === 'project_head')?.userName ?? '—'}
            projectManager={project.projectManager || '—'}
            sites={siteFacts(project)}
            fileCount={project.epcAttachments?.length ?? 0}
            departmentCount={activeOrgUnits.length}
          />
          <BriefingNotesCard
            {...(project.description ? { description: project.description } : {})}
            {...(project.remarks ? { remarks: project.remarks } : {})}
          />
        </div>
      </div>
    </div>
  );
}
